// TrackFiji V3.3.0 — map tools: toolbar (Zones · Ruler · Address), the ruler and the address card.
import { useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Marker, type GeoJSONSource, type Map as MLMap, type MapMouseEvent } from "maplibre-gl";
import { dms, fmtDistance, googleMapsLink, metres, pathMetres, type LngLat } from "../../lib/geo";
import { zonesAt, zoneStack } from "../../lib/geo";
import type { Zone } from "../../lib/org";

export type Tool = "zones" | "places" | "find" | "ruler" | "address" | null;

const ICON: Record<Exclude<Tool, null>, ReactNode> = {
  zones: <svg viewBox="0 0 24 24"><path d="M12 3.5 20 8v8l-8 4.5L4 16V8z" /><circle cx="12" cy="12" r="2.2" /></svg>,
  ruler: <svg viewBox="0 0 24 24"><path d="M3.5 15.5 15.5 3.5l5 5-12 12z" /><path d="M7.5 11.5l2 2M10 9l1.5 1.5M12.5 6.5l2 2M15 4l1.5 1.5" /></svg>,
  address: <svg viewBox="0 0 24 24"><path d="M12 21s-6.5-5.6-6.5-11a6.5 6.5 0 0 1 13 0c0 5.4-6.5 11-6.5 11z" /><circle cx="12" cy="10" r="2.4" /></svg>,
  places: <svg viewBox="0 0 24 24"><path d="M6.5 3.5h11v17l-5.5-4-5.5 4z" /><path d="M9.5 8.5h5" /></svg>,
  find: <svg viewBox="0 0 24 24"><circle cx="10.5" cy="10.5" r="6" /><path d="m15 15 5.5 5.5" /><path d="M10.5 7.5v6M7.5 10.5h6" /></svg>,
};
const LABEL: Record<Exclude<Tool, null>, string> = { zones: "Zones", places: "Saved places", find: "Find a location (coordinates or Google Maps link)",
  ruler: "Measure distance", address: "Show location (coordinates)" };

export function MapToolbar({ active, onChange }: { active: Tool; onChange: (t: Tool) => void }) {
  return (
    <div className="map-tools" role="toolbar" aria-label="Map tools">
      {(["zones", "places", "find", "ruler", "address"] as const).map((t) => (
        <button key={t} type="button" className={active === t ? "on" : ""} title={LABEL[t]} aria-label={LABEL[t]} aria-pressed={active === t}
          onClick={() => onChange(active === t ? null : t)}>{ICON[t]}</button>
      ))}
    </div>
  );
}

/** Shared floating panel for the active tool (top-right, left of the toolbar). */
export function ToolPanel({ title, onClose, children, footer }: { title: string; onClose: () => void; children: ReactNode; footer?: ReactNode }) {
  return (
    <section className="tool-panel" aria-label={title}>
      <header><strong>{title}</strong><button className="x" onClick={onClose} aria-label="Close">×</button></header>
      <div className="tool-body">{children}</div>
      {footer && <footer>{footer}</footer>}
    </section>
  );
}

// ---------------------------------------------------------------- ruler
export function RulerTool({ map, onClose }: { map: MLMap; onClose: () => void }) {
  const [pts, setPts] = useState<LngLat[]>([]);
  const [hover, setHover] = useState<LngLat | null>(null);
  const [done, setDone] = useState(false);
  const labels = useRef<Marker[]>([]);

  useEffect(() => {
    const empty = { type: "FeatureCollection" as const, features: [] };
    if (!map.getSource("tf-ruler")) map.addSource("tf-ruler", { type: "geojson", data: empty });
    if (!map.getLayer("tf-ruler-line")) map.addLayer({ id: "tf-ruler-line", type: "line", source: "tf-ruler", filter: ["==", "$type", "LineString"],
      paint: { "line-color": "#111827", "line-width": 2.5, "line-dasharray": [2, 1.5] } });
    if (!map.getLayer("tf-ruler-pts")) map.addLayer({ id: "tf-ruler-pts", type: "circle", source: "tf-ruler", filter: ["==", "$type", "Point"],
      paint: { "circle-radius": 4.5, "circle-color": "#fff", "circle-stroke-color": "#111827", "circle-stroke-width": 2 } });
    map.getCanvas().style.cursor = "crosshair";
    map.doubleClickZoom.disable();
    return () => {
      for (const id of ["tf-ruler-line", "tf-ruler-pts"]) if (map.getLayer(id)) map.removeLayer(id);
      if (map.getSource("tf-ruler")) map.removeSource("tf-ruler");
      labels.current.forEach((m) => m.remove()); labels.current = [];
      map.getCanvas().style.cursor = "";
      map.doubleClickZoom.enable();
    };
  }, [map]);

  useEffect(() => {
    const click = (e: MapMouseEvent) => {
      const p: LngLat = [e.lngLat.lng, e.lngLat.lat];
      if (done) { setPts([p]); setDone(false); return; }
      setPts((x) => [...x, p]);
    };
    const dbl = (e: MapMouseEvent) => {
      e.preventDefault();
      // a double-click also fires two clicks: drop the extra point(s) at the same spot
      setPts((x) => {
        const y = [...x];
        const px = (p: LngLat) => map.project(p);
        while (y.length > 1) {
          const a = px(y[y.length - 1]), b = px(y[y.length - 2]);
          if (Math.hypot(a.x - b.x, a.y - b.y) < 6) y.pop(); else break;
        }
        return y;
      });
      setDone(true); setHover(null);
    };
    const move = (e: MapMouseEvent) => { if (!done) setHover([e.lngLat.lng, e.lngLat.lat]); };
    const key = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "Backspace" || (e.key === "z" && (e.ctrlKey || e.metaKey))) setPts((x) => x.slice(0, -1));
      if (e.key === "Enter") setDone(true);
    };
    map.on("click", click); map.on("dblclick", dbl); map.on("mousemove", move);
    window.addEventListener("keydown", key);
    return () => { map.off("click", click); map.off("dblclick", dbl); map.off("mousemove", move); window.removeEventListener("keydown", key); };
  }, [map, done, onClose]);

  // draw
  useEffect(() => {
    const line = done || !hover || !pts.length ? pts : [...pts, hover];
    const src = map.getSource("tf-ruler") as GeoJSONSource | undefined;
    src?.setData({ type: "FeatureCollection", features: [
      ...(line.length > 1 ? [{ type: "Feature" as const, properties: {}, geometry: { type: "LineString" as const, coordinates: line } }] : []),
      ...pts.map((p) => ({ type: "Feature" as const, properties: {}, geometry: { type: "Point" as const, coordinates: p } })),
    ] });
    labels.current.forEach((m) => m.remove()); labels.current = [];
    let run = 0;
    pts.forEach((p, i) => {
      if (i > 0) run += metres(pts[i - 1], p);
      if (i === 0) return;
      const el = document.createElement("div");
      el.className = "ruler-label";
      el.textContent = fmtDistance(run);
      labels.current.push(new Marker({ element: el, anchor: "bottom", offset: [0, -8] }).setLngLat(p).addTo(map));
    });
  }, [map, pts, hover, done]);

  const total = pathMetres(pts);
  const live = !done && hover && pts.length ? total + metres(pts[pts.length - 1], hover) : null;
  return (
    <ToolPanel title="Measure distance" onClose={onClose}
      footer={<>
        <button className="btn-ghost small" disabled={!pts.length} onClick={() => setPts((x) => x.slice(0, -1))}>Undo</button>
        <button className="btn-ghost small" disabled={!pts.length} onClick={() => { setPts([]); setDone(false); }}>Clear</button>
        <button className="btn-primary small" onClick={onClose}>Done</button>
      </>}>
      <div className="ruler-total mono">{fmtDistance(live ?? total)}</div>
      <p className="sub">{pts.length === 0 ? "Click the map to start measuring." : done ? `${pts.length - 1} segment${pts.length === 2 ? "" : "s"}. Click the map to start again.`
        : "Click to add points · double-click or Enter to finish · Backspace to undo."}</p>
    </ToolPanel>
  );
}

// ---------------------------------------------------------------- address card
export type Spot = { lat: number; lng: number; title?: string; placeId?: string };

/** Location card anchored to a map point (V3.4.0: no street-address lookup). Opens from the Location tool,
 * a right-click, or ⋯ → Show location. Shows coordinates (decimal + DMS), the zone(s) it is in, and a Google Maps link. */
export function AddressCard({ map, spot, zones = [], onClose, onSave, saveLabel = "Save place", note }: { map: MLMap; spot: Spot; zones?: Zone[]; onClose: () => void;
  onSave?: () => void; saveLabel?: string; note?: string | null }) {
  const [el] = useState(() => document.createElement("div"));
  const [copied, setCopied] = useState<string | null>(null);
  useEffect(() => {
    const mk = new Marker({ element: el, anchor: "bottom", offset: [0, -6] }).setLngLat([spot.lng, spot.lat]).addTo(map);
    return () => { mk.remove(); };
  }, [map, el, spot.lat, spot.lng]);
  useEffect(() => {
    const key = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [onClose]);
  const coords = `${spot.lat.toFixed(6)}, ${spot.lng.toFixed(6)}`;
  const link = googleMapsLink(spot.lat, spot.lng);
  const inZones = zonesAt(zones, spot.lat, spot.lng);
  const copy = async (what: string, v: string) => {
    try { await navigator.clipboard.writeText(v); setCopied(what); setTimeout(() => setCopied(null), 1500); } catch { /* clipboard blocked */ }
  };
  return createPortal(
    <div className="addr-card" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()} onContextMenu={(e) => e.stopPropagation()}>
      <header><strong>{spot.title ?? "This location"}</strong><button className="x" onClick={onClose} aria-label="Close">×</button></header>
      <dl>
        <dt>Coordinates</dt><dd className="mono">{coords}</dd>
        <dt>DMS</dt><dd className="mono">{dms(spot.lat, spot.lng)}</dd>
        {inZones.length > 0 && <><dt>Zone</dt><dd>{zoneStack(inZones)}</dd></>}
        {note && <><dt>Notes</dt><dd>{note}</dd></>}
      </dl>
      <div className="addr-actions">
        <button className="btn-ghost small" onClick={() => copy("coords", coords)}>{copied === "coords" ? "Copied" : "Copy coordinates"}</button>
        <button className="btn-ghost small" onClick={() => copy("link", link)}>{copied === "link" ? "Copied" : "Copy Google Maps link"}</button>
        <a className="btn-ghost small" href={link} target="_blank" rel="noopener noreferrer">Open in Google Maps ↗</a>
        {onSave && <button className="btn-primary small" onClick={onSave}>{saveLabel}</button>}
      </div>
      <i className="addr-tip" />
    </div>,
    el,
  );
}

/** Wires the Address tool (click), right-click anywhere on the map, and left-click elsewhere to close the card. */
export function useAddressPicker(map: MLMap | null, toolOn: boolean, setSpot: (s: Spot | null) => void, hasSpot = false) {
  useEffect(() => {
    if (!map) return;
    const ctx = (e: MapMouseEvent) => { e.preventDefault(); setSpot({ lat: e.lngLat.lat, lng: e.lngLat.lng }); };
    // Address tool on: a click shows that point. Otherwise a left-click on the map just closes an open card.
    const click = (e: MapMouseEvent) => {
      if (toolOn) setSpot({ lat: e.lngLat.lat, lng: e.lngLat.lng });
      else if (hasSpot && e.originalEvent.button === 0) setSpot(null);
    };
    map.on("contextmenu", ctx);
    map.on("click", click);
    if (toolOn) map.getCanvas().style.cursor = "crosshair";
    return () => {
      map.off("contextmenu", ctx);
      map.off("click", click);
      if (toolOn) map.getCanvas().style.cursor = "";
    };
  }, [map, toolOn, setSpot, hasSpot]);
}
